import { type PermissionKey } from '@aysar/contracts';
import {
  BadgeCheck,
  BadgePercent,
  Building2,
  BellRing,
  BookOpenText,
  CalendarCheck,
  CalendarRange,
  CircleDollarSign,
  CreditCard,
  Flag,
  FlaskConical,
  Gavel,
  GraduationCap,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Plug,
  Receipt,
  Scale,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Ticket,
  TicketPercent,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react';

import { useAwaitingReceiptsCount } from '@/shared/hooks/useAwaitingReceiptsCount/useAwaitingReceiptsCount';

import { PRIVATE_ROUTES } from './route.constant';

/** The payments row's count, as the badge slot wants it. */
function useAwaitingReceiptsBadge(): number | undefined {
  return useAwaitingReceiptsCount().count;
}

export interface NavItem {
  /** `common:nav.*` key. Translated by the Sidebar, not here. */
  labelKey: string;
  to: string;
  icon: LucideIcon;
  /**
   * The operator needs ANY of these to see the item. An item with none is
   * visible to every signed-in operator.
   *
   * Hiding is cosmetic. The route behind it carries `PermissionRoute` and the
   * endpoints behind that carry `@RequirePermissions` — this list only spares
   * the operator from clicking into a wall (N22).
   */
  anyOf?: readonly PermissionKey[];
  /**
   * A live count worn on the row (F3) — work waiting behind this door. A hook,
   * because the number is a subscription (polled, permission-gated) and only
   * the module that owns the list knows how to count it; the sidebar renders
   * it through `SidebarNavBadge`, whose one job is to call it unconditionally.
   * `undefined` from the hook draws nothing; `0` draws nothing either.
   */
  useBadge?: () => number | undefined;
  children?: readonly NavItem[];
}

/**
 * The sidebar's contents — in `lib/`, beside the route constants, for the same
 * reason the event bus is: `shared/` renders it and modules contribute pages to
 * it, so it cannot live in either without inverting a dependency (W2).
 *
 * A new module adds a line here plus a route entry. It never edits `Sidebar`.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    to: PRIVATE_ROUTES.DASHBOARD,
    icon: LayoutDashboard,
  },
  {
    labelKey: 'nav.members',
    to: PRIVATE_ROUTES.MEMBERS,
    icon: Users,
    anyOf: ['members.read'],
  },
  {
    labelKey: 'nav.admins',
    to: PRIVATE_ROUTES.ADMINS,
    icon: ShieldCheck,
    anyOf: ['access.read', 'members.read'],
    children: [
      {
        labelKey: 'nav.staff',
        to: PRIVATE_ROUTES.ADMINS_STAFF,
        icon: ShieldCheck,
        anyOf: ['members.read'],
      },
      {
        labelKey: 'nav.roles',
        to: PRIVATE_ROUTES.ADMINS_ROLES,
        icon: ShieldCheck,
        anyOf: ['access.read'],
      },
    ],
  },
  {
    // TWO entries, because there are two questions: "who is waiting" and "what
    // are we offering them". `verification.review` opens both — a reviewer needs
    // to see who is coming and when — and PUBLISHING a window is
    // `verification.manage`, gated on the controls inside the calendar rather
    // than on the route, so a reviewer gets the whole surface and no action that
    // would 403 (P10.6j).
    labelKey: 'nav.verification',
    to: PRIVATE_ROUTES.VERIFICATION,
    icon: BadgeCheck,
    anyOf: ['verification.review'],
    children: [
      {
        labelKey: 'nav.verificationQueue',
        to: PRIVATE_ROUTES.VERIFICATION,
        icon: BadgeCheck,
        anyOf: ['verification.review'],
      },
      {
        labelKey: 'nav.verificationInterviews',
        to: PRIVATE_ROUTES.VERIFICATION_INTERVIEWS,
        icon: CalendarRange,
        anyOf: ['verification.review'],
      },
    ],
  },
  {
    labelKey: 'nav.trust',
    to: PRIVATE_ROUTES.TRUST,
    icon: ShieldAlert,
    anyOf: ['trust.review'],
    children: [
      {
        labelKey: 'nav.trustQueue',
        to: PRIVATE_ROUTES.TRUST,
        icon: ShieldAlert,
        anyOf: ['trust.review'],
      },
      {
        labelKey: 'nav.trustPolicy',
        to: PRIVATE_ROUTES.TRUST_POLICY,
        icon: ShieldAlert,
        anyOf: ['trust.review'],
      },
    ],
  },
  {
    labelKey: 'nav.violations',
    to: PRIVATE_ROUTES.VIOLATIONS,
    icon: Gavel,
    // The PARENT opens on the queue's key, so a review-only moderator still sees
    // the section. The catalogue child carries the wider key and disappears for
    // them rather than leading to a wall.
    anyOf: ['violations.review'],
    children: [
      {
        labelKey: 'nav.violationQueue',
        to: PRIVATE_ROUTES.VIOLATIONS,
        icon: Gavel,
        anyOf: ['violations.review'],
      },
      {
        labelKey: 'nav.violationTypes',
        to: PRIVATE_ROUTES.VIOLATION_TYPES,
        icon: Gavel,
        anyOf: ['violations.manage'],
      },
    ],
  },
  {
    labelKey: 'nav.notifications',
    icon: BellRing,
    // The parent opens on the copy screen — there is no bare `/notifications`
    // path, exactly as the Trust and Violations parents open on their queues.
    to: PRIVATE_ROUTES.NOTIFICATION_TEMPLATES,
    anyOf: ['notifications.read'],
    children: [
      {
        labelKey: 'nav.notificationTemplates',
        to: PRIVATE_ROUTES.NOTIFICATION_TEMPLATES,
        icon: BellRing,
        anyOf: ['notifications.read'],
      },
      {
        labelKey: 'nav.notificationDeliveries',
        to: PRIVATE_ROUTES.NOTIFICATION_DELIVERIES,
        icon: BellRing,
        anyOf: ['notifications.read'],
      },
      {
        labelKey: 'nav.notificationCampaigns',
        to: PRIVATE_ROUTES.NOTIFICATION_CAMPAIGNS,
        icon: BellRing,
        anyOf: ['notifications.read'],
      },
      {
        labelKey: 'nav.notificationDefaults',
        to: PRIVATE_ROUTES.NOTIFICATION_DEFAULTS,
        icon: BellRing,
        anyOf: ['notifications.read'],
      },
    ],
  },
  {
    // THREE children, and the test each one had to pass is the same: can this
    // thing be REACHED without an entry of its own?
    //
    // P22b1 wrote "two, and no more", because a lesson and a course's test are
    // reached through the course they belong to, and a sub-tree naming every
    // sub-resource would offer four ways into one page. Two things have since
    // failed that test rather than argued their way past it. A **standalone**
    // test has no course to be reached through (`tests.courseId` is nullable by
    // design), so without `nav.tests` the portal could create one it could never
    // find again. A **discount rule** names a course *or* a test (P22b3), so the
    // course page's own panel — which does exist, and is where an author starts
    // — can only ever show the rules that pay for courses; the ones that pay for
    // a standalone test would be unreachable by exactly the same argument.
    //
    // FOUR now. Grants and attempts (P22b4) are support surfaces about a MEMBER
    // rather than authoring surfaces about a course, so neither is reachable
    // through a course at all — and they earned **one** entry between them, not
    // two, by being one screen with two tabs over a shared member filter. The
    // support question is "why has this member not got their discount?", which
    // is answered by looking at both. Rewrite this comment again if a fifth is
    // proposed — the test is above, not the count.
    labelKey: 'nav.courses',
    to: PRIVATE_ROUTES.COURSES,
    icon: GraduationCap,
    anyOf: ['courses.read'],
    children: [
      {
        labelKey: 'nav.courseCatalogue',
        to: PRIVATE_ROUTES.COURSES,
        icon: GraduationCap,
        anyOf: ['courses.read'],
      },
      {
        labelKey: 'nav.tests',
        to: PRIVATE_ROUTES.TESTS,
        icon: GraduationCap,
        anyOf: ['courses.read'],
      },
      {
        labelKey: 'nav.discountRules',
        to: PRIVATE_ROUTES.DISCOUNT_RULES,
        icon: BadgePercent,
        anyOf: ['courses.read'],
      },
      {
        labelKey: 'nav.memberDiscounts',
        to: PRIVATE_ROUTES.MEMBER_DISCOUNTS,
        icon: Ticket,
        anyOf: ['courses.read'],
      },
    ],
  },
  {
    // The group this became in P25d2, exactly as the leaf it replaced predicted:
    // the booking queue is the second screen that can be reached on its own, and
    // that is the test. The availability calendar still is NOT a third — it
    // lives at `/venues/:id/availability`, so there is no id to put in a global
    // sidebar and it is reached from the venue itself.
    //
    // The parent opens on the BOOKINGS queue, which is the module's front door
    // and the screen an operator wants most days; venues are what the queue is
    // arranged against. Same shape as the Trust, Violations and Notifications
    // parents — there is no bare `/meetings` group path, and the parent's `to`
    // is its first child's.
    labelKey: 'nav.meetings',
    to: PRIVATE_ROUTES.MEETINGS,
    icon: Building2,
    // Both children read on the same key, so the parent carries it too — there
    // is no narrower half here to open the section for.
    anyOf: ['meetings.read'],
    children: [
      {
        labelKey: 'nav.bookings',
        to: PRIVATE_ROUTES.MEETINGS,
        icon: CalendarCheck,
        anyOf: ['meetings.read'],
      },
      {
        labelKey: 'nav.venues',
        to: PRIVATE_ROUTES.VENUES,
        icon: Building2,
        anyOf: ['meetings.read'],
      },
    ],
  },
  {
    // TWO entries, not one: the catalogue answers "what do we sell" and the
    // pricing screen answers "for how much, and what does paying buy" — two
    // questions, two permissions (`billing.read` opens both, but the prices
    // inside need `settings.read`), and an operator arrives holding one of them.
    // The parent is gated on `billing.read`, so a member-support account with
    // neither sees no billing group at all rather than a group it cannot open.
    labelKey: 'nav.subscriptions',
    to: PRIVATE_ROUTES.SUBSCRIPTIONS,
    icon: CreditCard,
    anyOf: ['billing.read'],
    children: [
      {
        labelKey: 'nav.subscriptionPlans',
        to: PRIVATE_ROUTES.SUBSCRIPTIONS,
        icon: CreditCard,
        anyOf: ['billing.read'],
      },
      {
        labelKey: 'nav.subscriptionPricing',
        to: PRIVATE_ROUTES.SUBSCRIPTION_PRICING,
        icon: CircleDollarSign,
        anyOf: ['billing.read'],
      },
      {
        // A third child rather than a top-level group: it is the same money,
        // read by the same permission. What separates it from the two above is
        // that this is a WORKING LIST somebody lives in, while a catalogue and a
        // price board are edited a few times a year.
        labelKey: 'nav.payments',
        to: PRIVATE_ROUTES.PAYMENTS,
        icon: Receipt,
        anyOf: ['billing.read'],
        // Receipts members sent from the app, waiting for a decision (F3) — the
        // in-portal signal that reaches an operator on any page.
        useBadge: useAwaitingReceiptsBadge,
      },
      {
        // A fourth child beside the direct queue, not under it: the two are the
        // platform's TWO payment channels answering the same question — one is
        // what a human recorded, the other is what a store sent — and both are
        // `billing.read`. The payload inside needs `billing.manage`, but gating
        // the nav entry on it would hide the diagnosis from the support role the
        // screen exists for.
        labelKey: 'nav.billingEvents',
        to: PRIVATE_ROUTES.BILLING_EVENTS,
        icon: Webhook,
        anyOf: ['billing.read'],
      },
      {
        // After the two channels and before what they grant: the ledger is the
        // one list both channels write — what money did, whichever door it came
        // through — and an operator reads it to answer "did they pay" before
        // asking entitlements "did they get it" (P31b).
        labelKey: 'nav.ledger',
        to: PRIVATE_ROUTES.LEDGER,
        icon: BookOpenText,
        anyOf: ['billing.read'],
      },
      {
        // A fifth child, and the one the other four lead TO: the catalogue says
        // what is sold, the price board what it costs, and the two channels who
        // paid — this says what a member actually holds as a result, which is
        // the question support is really asking in all four cases. Same
        // `billing.read`; the grant and the revoke on it are gated on the
        // control instead (N22).
        labelKey: 'nav.entitlements',
        to: PRIVATE_ROUTES.ENTITLEMENTS,
        icon: KeyRound,
        anyOf: ['billing.read'],
      },
      {
        // A sixth child, beside the price board rather than the payment records:
        // an offer code is how a discount reaches an IAP **price**, which makes
        // it the pricing screen's store-side counterpart. `billing.read` opens
        // it; the rule picker inside additionally needs `courses.read` and is
        // withheld without it (P21b), which is a state the screen explains
        // rather than a reason to gate the whole entry.
        labelKey: 'nav.offerCodes',
        to: PRIVATE_ROUTES.OFFER_CODES,
        icon: TicketPercent,
        anyOf: ['billing.read'],
      },
    ],
  },
  {
    labelKey: 'nav.questions',
    to: PRIVATE_ROUTES.QUESTIONS,
    icon: ListChecks,
    anyOf: ['questions.read'],
  },
  {
    labelKey: 'nav.legal',
    to: PRIVATE_ROUTES.LEGAL,
    icon: Scale,
    anyOf: ['legal.read'],
  },
  {
    labelKey: 'nav.settings',
    to: PRIVATE_ROUTES.SETTINGS,
    icon: SlidersHorizontal,
    anyOf: ['settings.read'],
  },

  {
    labelKey: 'nav.integrations',
    to: PRIVATE_ROUTES.INTEGRATIONS,
    icon: Plug,
    anyOf: ['integrations.read'],
  },
  {
    labelKey: 'nav.moderation',
    to: PRIVATE_ROUTES.MODERATION,
    icon: Flag,
    anyOf: ['moderation.review'],
  },
  // The Overlay Lab, in a development build only — spread rather than pushed so
  // `NAV_ITEMS` stays one frozen declaration, and folded away entirely in a
  // production build, where the route behind it does not exist either.
  ...(import.meta.env.DEV
    ? [{ labelKey: 'nav.kitLab', to: PRIVATE_ROUTES.KIT_LAB, icon: FlaskConical }]
    : []),
];
